// AdminResetPassword — accepts the 6-digit code sent by e-mail, validates
// the new password, and submits to /api/admin/auth/reset-password.
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(params.get('email') || '');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.includes('@')) return setError('Informe um e-mail válido.');
    if (!/^\d{6}$/.test(codigo)) return setError('O código deve ter exatamente 6 dígitos.');
    if (novaSenha.length < 8) return setError('A senha deve ter pelo menos 8 caracteres.');
    if (novaSenha !== confirmar) return setError('As senhas não conferem.');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        codigo: codigo.trim(),
        nova_senha: novaSenha,
        confirmar_senha: confirmar,
      });
      setDone(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível redefinir a senha. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md border border-[#E2E8F0] p-6"
           data-testid="admin-reset-page">
        <div className="text-center mb-5">
          <div className="w-12 h-12 mx-auto bg-[#1E3A5F] rounded-md flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-black text-[#0F1C2E]">Definir nova senha</h1>
          <p className="text-xs text-[#475569] mt-1">Painel administrativo · Tô de Olho</p>
        </div>

        {done ? (
          <div className="space-y-3" data-testid="admin-reset-done">
            <div className="flex items-start gap-2 p-3 rounded bg-[#DCFCE7] border border-[#BBF7D0] text-[#006030] text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Senha redefinida com sucesso. Redirecionando para o login…</span>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-start gap-2 p-2.5 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs"
                   data-testid="admin-reset-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">E-mail do admin</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                  data-testid="admin-reset-email"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">Código de 6 dígitos</span>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-2xl font-black text-center tracking-[0.5em] focus:outline-none focus:border-[#1E3A5F]"
                  data-testid="admin-reset-codigo"
                />
                <p className="text-[10px] text-[#94A3B8] mt-1">Enviado por e-mail. Expira em 15 minutos.</p>
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">Nova senha</span>
                <div className="relative mt-1">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    required
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    className="w-full px-3 py-2 pr-10 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                    data-testid="admin-reset-nova"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                    aria-label={showPwd ? 'Esconder senha' : 'Mostrar senha'}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-1">Mínimo de 8 caracteres.</p>
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">Confirmar nova senha</span>
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={confirmar}
                  onChange={(e) => setConfirmar(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                  data-testid="admin-reset-confirmar"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1E3A5F] hover:bg-[#16304b] text-white font-bold py-2 rounded text-sm disabled:opacity-60 flex items-center justify-center"
                data-testid="admin-reset-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar nova senha'}
              </button>
            </form>
            <Link to="/admin/forgot-password"
                  className="mt-3 block text-center text-xs text-[#475569] hover:text-[#1E3A5F]">
              Não recebeu o código? Pedir novamente
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
